/**
 * Consolidated distribution chart card with Type/State/Country tabs and Bar/Pie toggle
 */

import { useState } from 'react';
import './DistributionCard.css';

const PALETTE = [
  '#c4956a', '#5c2a0e', '#8b5a2b', '#d4a96a', '#3d1a08',
  '#e8c49a', '#7a3b1e', '#b87333', '#9e6b3f', '#6b2d0f'
];

const GROUPS = [
  { key: 'type',    label: 'Type' },
  { key: 'price',   label: 'Price' },
  { key: 'state',   label: 'State' },
  { key: 'country', label: 'Country' }
];

const EMPTY_MESSAGES = {
  type:    'Add bottles to see type distribution',
  price:   'Add bottles with a purchase price to see price distribution',
  state:   'Add bottles with distillery states to see state distribution',
  country: 'Add bottles with distilleries to see country distribution'
};

function BarChart({ data, onItemClick }) {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  const clickable = typeof onItemClick === 'function';

  const handleActivate = (item) => {
    if (clickable) onItemClick(item);
  };

  return (
    <div className="distribution-card__bars">
      {data.map((item, index) => (
        <div
          key={index}
          className={`distribution-card__bar-row${clickable ? ' distribution-card__bar-row--clickable' : ''}`}
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={clickable ? () => handleActivate(item) : undefined}
          onKeyDown={clickable ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleActivate(item);
            }
          } : undefined}
          title={clickable ? `View bottles: ${item.label}` : undefined}
        >
          <div className="distribution-card__bar-label" title={item.label}>{item.label}</div>
          <div className="distribution-card__bar-container">
            <div
              className="distribution-card__bar"
              style={{ width: `${(item.value / maxValue) * 100}%` }}
            />
            <span className="distribution-card__bar-value">{item.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function PieChart({ data, onItemClick }) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;

  const SIZE = 200;
  const CENTER = SIZE / 2;
  const RADIUS = 85;

  let currentAngle = -Math.PI / 2;
  const slices = data.map((item, i) => {
    const fraction = item.value / total;
    const angle = fraction * 2 * Math.PI;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    let pathD;
    if (fraction >= 1) {
      // Full circle: draw as two half-arcs to avoid degenerate path
      pathD = [
        `M ${CENTER} ${CENTER - RADIUS}`,
        `A ${RADIUS} ${RADIUS} 0 1 1 ${CENTER} ${CENTER + RADIUS}`,
        `A ${RADIUS} ${RADIUS} 0 1 1 ${CENTER} ${CENTER - RADIUS}`,
        'Z'
      ].join(' ');
    } else {
      const x1 = CENTER + RADIUS * Math.cos(startAngle);
      const y1 = CENTER + RADIUS * Math.sin(startAngle);
      const x2 = CENTER + RADIUS * Math.cos(endAngle);
      const y2 = CENTER + RADIUS * Math.sin(endAngle);
      const largeArc = angle > Math.PI ? 1 : 0;
      pathD = `M ${CENTER} ${CENTER} L ${x1} ${y1} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    }

    return {
      ...item,
      pathD,
      color: PALETTE[i % PALETTE.length],
      percent: Math.round(fraction * 100)
    };
  });

  const clickable = typeof onItemClick === 'function';

  const handleActivate = (slice) => {
    if (clickable) onItemClick(slice);
  };

  return (
    <div className="distribution-card__pie">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="distribution-card__pie-svg"
        aria-hidden={clickable ? undefined : 'true'}
      >
        {slices.map((slice, i) => (
          <path
            key={i}
            d={slice.pathD}
            fill={slice.color}
            stroke="var(--color-bg-surface)"
            strokeWidth="1.5"
            className={clickable ? 'distribution-card__pie-slice--clickable' : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={clickable ? `View bottles: ${slice.label}` : undefined}
            onClick={clickable ? () => handleActivate(slice) : undefined}
            onKeyDown={clickable ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleActivate(slice);
              }
            } : undefined}
          >
            <title>{`${slice.label}: ${slice.value}`}</title>
          </path>
        ))}
      </svg>
      <div className="distribution-card__legend">
        {slices.map((slice, i) => (
          <div
            key={i}
            className={`distribution-card__legend-item${clickable ? ' distribution-card__legend-item--clickable' : ''}`}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => handleActivate(slice) : undefined}
            onKeyDown={clickable ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleActivate(slice);
              }
            } : undefined}
            title={clickable ? `View bottles: ${slice.label}` : undefined}
          >
            <span
              className="distribution-card__legend-dot"
              style={{ backgroundColor: slice.color }}
            />
            <span className="distribution-card__legend-label" title={slice.label}>{slice.label}</span>
            <span className="distribution-card__legend-value">{slice.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Card with Type/State/Country group selector and Bar/Pie chart toggle
 * @param {Object} props
 * @param {Array} props.typeData    - [{label, value}]
 * @param {Array} props.priceData   - [{label, value}]
 * @param {Array} props.stateData   - [{label, value}]
 * @param {Array} props.countryData - [{label, value}]
 * @param {Function} [props.onSegmentClick] - Called with (groupKey, item) when a bar/slice is clicked
 */
export default function DistributionCard({ typeData = [], priceData = [], stateData = [], countryData = [], onSegmentClick }) {
  const [group, setGroup] = useState('type');
  const [chartType, setChartType] = useState('bar');

  const dataMap = { type: typeData, price: priceData, state: stateData, country: countryData };
  const data = dataMap[group];
  const handleItemClick = typeof onSegmentClick === 'function'
    ? (item) => onSegmentClick(group, item)
    : undefined;

  return (
    <div className="distribution-card">
      <div className="distribution-card__header">
        <div className="distribution-card__tabs">
          {GROUPS.map(g => (
            <button
              key={g.key}
              className={`distribution-card__tab${group === g.key ? ' distribution-card__tab--active' : ''}`}
              onClick={() => setGroup(g.key)}
            >
              {g.label}
            </button>
          ))}
        </div>

        <div className="distribution-card__view-toggle">
          <button
            className={`distribution-card__view-btn${chartType === 'bar' ? ' distribution-card__view-btn--active' : ''}`}
            onClick={() => setChartType('bar')}
            title="Bar chart"
            aria-label="Bar chart"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="12" width="4" height="9" />
              <rect x="10" y="7" width="4" height="14" />
              <rect x="17" y="3" width="4" height="18" />
              <line x1="3" y1="21" x2="21" y2="21" />
            </svg>
          </button>
          <button
            className={`distribution-card__view-btn${chartType === 'pie' ? ' distribution-card__view-btn--active' : ''}`}
            onClick={() => setChartType('pie')}
            title="Pie chart"
            aria-label="Pie chart"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
              <path d="M22 12A10 10 0 0 0 12 2v10z" />
            </svg>
          </button>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="distribution-card__empty">{EMPTY_MESSAGES[group]}</div>
      ) : chartType === 'bar' ? (
        <BarChart data={data} onItemClick={handleItemClick} />
      ) : (
        <PieChart data={data} onItemClick={handleItemClick} />
      )}
    </div>
  );
}
